/**
 * JourneyHistoryModule — admin Journey History & Stop Points (ODE-P07-T06).
 *
 * A presentation slice over the VERIFIED ODE-P04-T09 server read model. It
 * renders, with the full REQ-090 state set:
 *
 *   pending       filters not yet submitted (nothing fetched)
 *   loading       read in flight
 *   empty         200 with zero journeys — explicit "no journey", never
 *                 fabricated
 *   ready         server page rendered (journey summaries only)
 *   unauthorized  401 — session missing/expired
 *   blocked       403 — role denied (server-enforced; the mirror is UX only)
 *   error         400/503/network/malformed payload — retryable
 *
 * Detail panel (REQ-040/041/042): the ordered route-point table, the derived
 * stop-point table (anchor coordinates, arrival/departure, dwell, sample
 * count), the derived distance + stop-count summary, an explicit truncation
 * notice when the server capped the polyline, the recorded tracking gaps
 * (coordinates always null — never fabricated) and a read-only panel with the
 * documented server derivation configuration.
 *
 * Nothing is derived here: the polyline order, stop points, dwell and
 * distance come from the server projection verbatim (approved DND — server
 * authoritative for derivation). All output carries stable `data-testid`s for
 * deterministic tests, and no status is encoded by color alone.
 *
 * Traces to: ODE-PRD-REQ-040, ODE-PRD-REQ-041, ODE-PRD-REQ-042,
 *            ODE-PRD-REQ-046, ODE-PRD-REQ-090
 */

import { useAuth } from '../../auth/AuthContext.js';
import type { JourneyHistoryDetail, JourneyHistorySummary } from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import type { ModuleState } from '../state.js';
import {
  JOURNEY_DERIVATION_CONFIG,
  JOURNEY_LIST_MAX_PAGE_SIZE,
  JOURNEY_MAX_POLYLINE_POINTS,
} from './derivation-config.js';
import {
  useJourneyHistory,
  type JourneyHistoryDetailState,
  type JourneyHistoryListState,
  type UseJourneyHistoryOptions,
} from './useJourneyHistory.js';

export interface JourneyHistorySeams {
  readonly list?: UseJourneyHistoryOptions['list'];
  readonly detail?: UseJourneyHistoryOptions['detail'];
}

/** Formats an epoch-milliseconds instant (or null) for display. */
export function formatEpochMs(value: number | null): string {
  return value === null ? '—' : new Date(value).toISOString();
}

/** Formats a derived distance (meters) — display only, never recomputed. */
export function formatMeters(value: number): string {
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)} km (${value.toFixed(1)} m)`;
  return `${value.toFixed(1)} m`;
}

/** Formats a derived dwell duration (milliseconds) for display. */
export function formatDwell(dwellMs: number): string {
  const seconds = dwellMs / 1_000;
  if (seconds >= 60) return `${(seconds / 60).toFixed(1)} menit (${seconds.toFixed(0)} s)`;
  return `${seconds.toFixed(1)} s`;
}

export function JourneyHistoryModule({
  seams = {},
}: {
  readonly seams?: JourneyHistorySeams;
} = {}): JSX.Element {
  const { session } = useAuth();
  const journeyHistory = useJourneyHistory({
    token: session?.token ?? null,
    ...(seams.list === undefined ? {} : { list: seams.list }),
    ...(seams.detail === undefined ? {} : { detail: seams.detail }),
  });
  const { form, setForm, state, detailState, validationError } = journeyHistory;
  const invalid = validationError !== null;

  return (
    <section aria-labelledby="journey-history-title" className="module">
      <h2 id="journey-history-title">Journey History</h2>
      <p className="module-description">
        Riwayat perjalanan dan titik henti (REQ-040/041/042/046) dari model baca
        server ODE-P04-T09 — daftar ringkas dengan filter driver/tanggal/journey
        dan halaman terbatas, lalu detail baca-saja berisi urutan titik rute,
        titik henti terderivasi, dan jarak terderivasi. Semua nilai berasal dari
        server; halaman ini tidak menghitung ulang apa pun.
      </p>

      <form
        className="journey-history-form"
        data-testid="journey-filter-form"
        onSubmit={(event) => {
          event.preventDefault();
          journeyHistory.submit();
        }}
      >
        <label>
          Driver
          <input
            data-testid="journey-filter-driver"
            value={form.driverId}
            onChange={(event) => setForm({ driverId: event.target.value })}
            placeholder="mis. driver-1"
          />
        </label>
        <label>
          Journey ID
          <input
            data-testid="journey-filter-journey-id"
            value={form.journeyId}
            onChange={(event) => setForm({ journeyId: event.target.value })}
            placeholder="UUID sesi tracking"
          />
        </label>
        <label>
          Dari tanggal (UTC)
          <input
            type="date"
            data-testid="journey-filter-from"
            value={form.from}
            onChange={(event) => setForm({ from: event.target.value })}
          />
        </label>
        <label>
          Sampai tanggal (UTC)
          <input
            type="date"
            data-testid="journey-filter-to"
            value={form.to}
            onChange={(event) => setForm({ to: event.target.value })}
          />
        </label>
        <label>
          Batas baris
          <input
            data-testid="journey-filter-limit"
            value={form.limit}
            onChange={(event) => setForm({ limit: event.target.value })}
            placeholder={`1..${JOURNEY_LIST_MAX_PAGE_SIZE}`}
          />
        </label>
        <div className="journey-history-actions">
          <button type="submit" data-testid="journey-filter-submit" disabled={invalid}>
            Cari
          </button>
          <button type="button" data-testid="journey-filter-reset" onClick={journeyHistory.reset}>
            Atur ulang
          </button>
        </div>
      </form>

      {invalid && (
        <p
          role="alert"
          className="journey-filter-invalid"
          data-testid="journey-filter-invalid"
        >
          Filter belum dapat dikirim ke server: {validationError}
        </p>
      )}

      {state.kind === 'pending' ? (
        <StateView state={{ kind: 'pending' }} />
      ) : state.kind === 'ready' ? (
        <JourneyList
          state={state}
          onOpenDetail={journeyHistory.openDetail}
          onNextPage={journeyHistory.nextPage}
          onPrevPage={journeyHistory.prevPage}
        />
      ) : (
        <StateView state={toModuleState(state)} onRetry={journeyHistory.submit} />
      )}

      <JourneyDetailPanel state={detailState} onClose={journeyHistory.closeDetail} />
    </section>
  );
}

/** Maps the list state to the shared REQ-090 renderer. */
function toModuleState(state: JourneyHistoryListState): ModuleState {
  switch (state.kind) {
    case 'pending':
      return { kind: 'pending' };
    case 'loading':
      return { kind: 'loading' };
    case 'ready':
      return { kind: 'empty', payload: state.result };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: state.message };
    case 'error':
      return { kind: 'error', message: state.message };
  }
}

function JourneyList({
  state,
  onOpenDetail,
  onNextPage,
  onPrevPage,
}: {
  readonly state: Extract<JourneyHistoryListState, { kind: 'ready' }>;
  readonly onOpenDetail: (journeyId: string) => void;
  readonly onNextPage: () => void;
  readonly onPrevPage: () => void;
}): JSX.Element {
  const { result } = state;
  if (result.journeys.length === 0) {
    return (
      <div data-testid="journey-list-empty" className="state state-empty">
        <h3>Belum ada data</h3>
        <p>Tidak ada perjalanan yang cocok dengan filter ini.</p>
      </div>
    );
  }
  const canPrev = result.page > 1;
  const canNext = result.hasMore;
  return (
    <div data-testid="journey-list">
      <table className="overview-table">
        <thead>
          <tr>
            <th scope="col">Journey ID</th>
            <th scope="col">Delivery Order</th>
            <th scope="col">Driver</th>
            <th scope="col">Status</th>
            <th scope="col">Mulai</th>
            <th scope="col">Selesai</th>
            <th scope="col">Sampel</th>
            <th scope="col">Jarak (REQ-042)</th>
            <th scope="col">Titik henti (REQ-041)</th>
            <th scope="col">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {result.journeys.map((journey) => (
            <JourneyRow key={journey.journeyId} journey={journey} onOpenDetail={onOpenDetail} />
          ))}
        </tbody>
      </table>
      <p className="journey-history-page" data-testid="journey-list-page">
        Halaman {result.page} · {result.journeys.length} dari {result.total} perjalanan · batas{' '}
        {result.limit} baris
      </p>
      <div className="journey-history-pagination">
        <button
          type="button"
          data-testid="journey-page-prev"
          onClick={onPrevPage}
          disabled={!canPrev}
        >
          Sebelumnya
        </button>
        <button
          type="button"
          data-testid="journey-page-next"
          onClick={onNextPage}
          disabled={!canNext}
        >
          Berikutnya
        </button>
        {!canNext && (
          <span className="journey-history-hint" data-testid="journey-page-end">
            Halaman terakhir — tidak ada perjalanan berikutnya.
          </span>
        )}
      </div>
    </div>
  );
}

function JourneyRow({
  journey,
  onOpenDetail,
}: {
  readonly journey: JourneyHistorySummary;
  readonly onOpenDetail: (journeyId: string) => void;
}): JSX.Element {
  return (
    <tr data-testid={`journey-row-${journey.journeyId}`}>
      <td>{journey.journeyId}</td>
      <td>{journey.deliveryOrderId}</td>
      <td>{journey.driverId}</td>
      <td data-testid={`journey-state-${journey.journeyId}`}>{journey.state}</td>
      <td>{formatEpochMs(journey.startedAtEpochMs)}</td>
      <td>{formatEpochMs(journey.endedAtEpochMs)}</td>
      <td data-testid={`journey-samples-${journey.journeyId}`}>{journey.sampleCount}</td>
      <td data-testid={`journey-distance-${journey.journeyId}`}>
        {formatMeters(journey.distanceMeters)}
      </td>
      <td data-testid={`journey-stops-${journey.journeyId}`}>{journey.stopPointCount}</td>
      <td>
        <button
          type="button"
          data-testid={`journey-detail-${journey.journeyId}`}
          onClick={() => onOpenDetail(journey.journeyId)}
        >
          Detail
        </button>
      </td>
    </tr>
  );
}

function JourneyDetailPanel({
  state,
  onClose,
}: {
  readonly state: JourneyHistoryDetailState;
  readonly onClose: () => void;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;

  return (
    <section aria-label="Detail perjalanan" data-testid="journey-detail-panel" className="journey-detail">
      <div className="journey-detail-head">
        <h3>Detail Perjalanan</h3>
        <button type="button" data-testid="journey-detail-close" onClick={onClose}>
          Tutup
        </button>
      </div>
      {state.kind === 'loading' && <StateView state={{ kind: 'loading' }} />}
      {state.kind === 'not_found' && (
        <div role="alert" data-testid="journey-detail-not-found" className="state state-error">
          <h3>Tidak ditemukan</h3>
          <p>Journey tidak dikenal.</p>
        </div>
      )}
      {state.kind === 'unauthorized' && <StateView state={{ kind: 'unauthorized' }} />}
      {state.kind === 'blocked' && <StateView state={{ kind: 'blocked', message: state.message }} />}
      {state.kind === 'error' && <StateView state={{ kind: 'error', message: state.message }} />}
      {state.kind === 'ready' && <JourneyDetailBody detail={state.detail} />}
    </section>
  );
}

function JourneyDetailBody({ detail }: { readonly detail: JourneyHistoryDetail }): JSX.Element {
  const truncated = detail.sampleCount > detail.polyline.length;
  return (
    <div data-testid="journey-detail-ready">
      <dl className="journey-detail-grid">
        <dt>Journey ID</dt>
        <dd data-testid="detail-journey-id">{detail.journeyId}</dd>
        <dt>Delivery Order</dt>
        <dd data-testid="detail-delivery-order">{detail.deliveryOrderId}</dd>
        <dt>Driver</dt>
        <dd data-testid="detail-driver">{detail.driverId}</dd>
        <dt>Status (REQ-040)</dt>
        <dd data-testid="detail-state">{detail.state}</dd>
        <dt>Mulai</dt>
        <dd data-testid="detail-started">{formatEpochMs(detail.startedAtEpochMs)}</dd>
        <dt>Selesai</dt>
        <dd data-testid="detail-ended">{formatEpochMs(detail.endedAtEpochMs)}</dd>
        <dt>Fix pertama</dt>
        <dd data-testid="detail-first-fix">{formatEpochMs(detail.firstFixAtEpochMs)}</dd>
        <dt>Fix terakhir</dt>
        <dd data-testid="detail-last-fix">{formatEpochMs(detail.lastFixAtEpochMs)}</dd>
        <dt>Jumlah sampel</dt>
        <dd data-testid="detail-sample-count">{detail.sampleCount}</dd>
      </dl>

      <section aria-label="Ringkasan terderivasi" data-testid="detail-derived-summary">
        <h4>Ringkasan terderivasi (server)</h4>
        <p data-testid="detail-distance">
          Jarak tempuh (REQ-042): {formatMeters(detail.distanceMeters)}
        </p>
        <p data-testid="detail-stop-count">
          Titik henti (REQ-041): {detail.stopPointCount}
        </p>
      </section>

      {truncated && (
        <p role="status" className="journey-truncation" data-testid="journey-polyline-truncated">
          Rute ditampilkan sebagian: {detail.polyline.length} dari {detail.sampleCount} titik
          (batas server {JOURNEY_MAX_POLYLINE_POINTS} titik). Jarak dan titik henti tetap
          dihitung server atas seluruh sampel.
        </p>
      )}

      <section aria-label="Titik rute" data-testid="detail-route">
        <h4>Urutan titik rute (REQ-040)</h4>
        {detail.polyline.length === 0 ? (
          <p data-testid="journey-route-empty">
            Tidak ada titik rute tersimpan untuk perjalanan ini — tidak ada koordinat yang
            dikarang.
          </p>
        ) : (
          <table className="overview-table">
            <thead>
              <tr>
                <th scope="col">Urutan</th>
                <th scope="col">Waktu</th>
                <th scope="col">Lintang</th>
                <th scope="col">Bujur</th>
                <th scope="col">Akurasi</th>
              </tr>
            </thead>
            <tbody data-testid="journey-route-table">
              {detail.polyline.map((point) => (
                <tr key={point.sequence} data-testid={`journey-route-${point.sequence}`}>
                  <td>{point.sequence}</td>
                  <td>{formatEpochMs(point.capturedAtEpochMs)}</td>
                  <td>{point.latitude}</td>
                  <td>{point.longitude}</td>
                  <td>{point.accuracyMeters === null ? '—' : `${point.accuracyMeters} m`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="Titik henti" data-testid="detail-stops">
        <h4>Titik henti terderivasi (REQ-041)</h4>
        {detail.stopPoints.length === 0 ? (
          <p data-testid="journey-stop-empty">
            Tidak ada titik henti terderivasi untuk perjalanan ini.
          </p>
        ) : (
          <table className="overview-table">
            <thead>
              <tr>
                <th scope="col">Titik henti</th>
                <th scope="col">Lintang anchor</th>
                <th scope="col">Bujur anchor</th>
                <th scope="col">Tiba</th>
                <th scope="col">Berangkat</th>
                <th scope="col">Dwell</th>
                <th scope="col">Sampel</th>
              </tr>
            </thead>
            <tbody data-testid="journey-stop-table">
              {detail.stopPoints.map((stop, index) => (
                <tr key={stop.stopId} data-testid={`journey-stop-${index}`}>
                  <td>{stop.stopId}</td>
                  <td>{stop.latitude}</td>
                  <td>{stop.longitude}</td>
                  <td>{formatEpochMs(stop.arrivedAtEpochMs)}</td>
                  <td>{formatEpochMs(stop.departedAtEpochMs)}</td>
                  <td>{formatDwell(stop.dwellMs)}</td>
                  <td>{stop.sampleCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="Celah rekaman" data-testid="detail-gaps">
        <h4>Celah rekaman</h4>
        <p data-testid="journey-gap-count">
          {detail.gaps.length} celah tercatat — koordinat celah selalu kosong (tidak pernah
          dikarang).
        </p>
        {detail.gaps.length > 0 && (
          <table className="overview-table">
            <thead>
              <tr>
                <th scope="col">Dari</th>
                <th scope="col">Sampai</th>
                <th scope="col">Alasan</th>
              </tr>
            </thead>
            <tbody data-testid="journey-gap-table">
              {detail.gaps.map((gap, index) => (
                <tr key={`${gap.fromEpochMs}-${index}`} data-testid={`journey-gap-${index}`}>
                  <td>{formatEpochMs(gap.fromEpochMs)}</td>
                  <td>{formatEpochMs(gap.toEpochMs)}</td>
                  <td>{gap.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="Konfigurasi derivasi" data-testid="journey-derivation-config">
        <h4>Konfigurasi derivasi server (baca-saja)</h4>
        <p className="journey-history-hint" data-testid="journey-derivation-config-note">
          Nilai kontrak default ODE-P04-T09 (REQ-041/042) — hanya informasi. Server tetap
          otoritatif; halaman ini tidak menerapkan konfigurasi apa pun.
        </p>
        <table className="overview-table">
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Default terdokumentasi</th>
              <th scope="col">Batas</th>
              <th scope="col">Requirement</th>
            </tr>
          </thead>
          <tbody data-testid="journey-derivation-config-table">
            {JOURNEY_DERIVATION_CONFIG.map((entry) => (
              <tr key={entry.key} data-testid={`journey-derivation-${entry.key}`}>
                <td>{entry.label}</td>
                <td>{entry.defaultValue}</td>
                <td>{entry.bounds}</td>
                <td>{entry.requirement}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
